import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  apiLogCsvUrl,
  apiLogQueryOptions,
  saveApiLogRetention,
  type ApiLogFilters,
  type ApiLogRow,
} from '../../api/settingsApiIntegration'
import { Forbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './ApiIntegrationPage.css'

/**
 * Settings -> INTEGRARE -> API Integration -> API log.
 *
 * Jurnalul apelurilor API (promis in oferta, auditul CEC): fiecare cerere la
 * API-ul de integrare (`/api/v4`, cheia de API) si la API-ul public al
 * portalului (`/api/v2/public`), cu cheia sau contul, firma, metoda, tiparul
 * rutei, codul, durata si IP-ul. Fara corpul cererii si fara secrete - deci
 * exportul CSV poate pleca la auditor ca atare.
 *
 * Filtrele se aplica la "Search", nu la fiecare tasta: fiecare cautare e o
 * cerere la baza, pe o tabela care creste cu fiecare apel.
 */

const EMPTY: ApiLogFilters = {}

/** Codul HTTP colorat dupa familie: 4xx portocaliu, 5xx rosu. */
export function statusClass(status: number): string {
  return status >= 500 ? 'al-status-5' : status >= 400 ? 'al-status-4' : ''
}

/** "integration" / "public", cum il citeste omul. */
export function apiName(app: number): string {
  return app === 2 ? 'integration' : 'public'
}

function Row({ row }: { row: ApiLogRow }) {
  return (
    <tr>
      <td>{row.at}</td>
      <td>{apiName(row.app)}</td>
      <td className="ak-key">{row.keyPrefix !== '' ? `${row.keyPrefix}…` : ''}</td>
      <td>{row.userId ?? ''}</td>
      <td>{row.companyId ?? ''}</td>
      <td>{row.method}</td>
      <td className="al-route">{row.route}</td>
      <td className={statusClass(row.status)}>{row.status}</td>
      <td style={{ textAlign: 'right' }}>{row.ms} ms</td>
      <td>{row.ip}</td>
    </tr>
  )
}

export function ApiLogPage() {
  const query = useQueryClient()
  const [draft, setDraft] = useState<ApiLogFilters>(EMPTY)
  const [filters, setFilters] = useState<ApiLogFilters>(EMPTY)
  const [retention, setRetention] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const log = useQuery(apiLogQueryOptions(filters))

  if (log.error instanceof ApiError && log.error.status === 403) return <Forbidden />
  const page = log.data
  if (!page) return <Spinner />

  const field = (name: keyof ApiLogFilters, label: string, type = 'text', width?: number) => (
    <label>
      {label}
      <input
        id={`al_${name}`}
        type={type}
        value={String(draft[name] ?? '')}
        style={width ? { width } : undefined}
        onChange={(e) => setDraft({ ...draft, [name]: e.target.value })}
      />
    </label>
  )

  const pages = Math.max(1, Math.ceil(page.total / page.pageSize))
  const go = (to: number) => setFilters({ ...filters, page: to })

  const keep = () => {
    const days = Number(retention || page.retentionDays)
    setBusy(true)
    void saveApiLogRetention(days)
      .then((done) => {
        setBusy(false)
        setRetention(String(done.retentionDays))
        void query.invalidateQueries({ queryKey: ['settings', 'api_integration', 'log'] })
        toast(`The API log keeps ${done.retentionDays} days.`, 'API log')
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(error instanceof ApiError && error.message ? error.message : 'The retention could not be saved.', 'API log', 'error')
      })
  }

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          {page.tabs.length > 0 ? <li className="menu-header">{page.tabs[0].group}</li> : null}
          {page.tabs.map((tab) => (
            <li className={tab.id === 'api_log' ? 'menu-tab active' : 'menu-tab'} key={tab.id}>
              <a id={tab.id} href={`/app/${tab.href}`}>
                <i className={tab.icon} aria-hidden="true"></i> {tab.label}
              </a>
            </li>
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        <div id="content">
          <h3 style={{ marginTop: 0 }}>API log</h3>
          <p className="ak-note" style={{ marginBottom: 14 }}>
            Every call to the integration API (API keys) and to the public API of the portal: when, which key or account,
            which company, the method, the route, the answer, how long it took and from where. Request bodies and secrets
            are never recorded.
            {page.unattributed ? ' As a platform administrator you also see the calls that belong to no company.' : ''}
          </p>

          <form
            className="al-filters"
            id="alFilters"
            onSubmit={(e) => {
              e.preventDefault()
              setFilters({ ...draft, page: 1 })
            }}
          >
            {field('from', 'From', 'date')}
            {field('to', 'To', 'date')}
            <label>
              API
              <select id="al_app" value={draft.app ?? ''} onChange={(e) => setDraft({ ...draft, app: e.target.value })}>
                <option value="">Both</option>
                <option value="2">Integration (/api/v4)</option>
                <option value="1">Public</option>
              </select>
            </label>
            {field('key', 'Key prefix', 'text', 120)}
            {field('user', 'User ID', 'text', 80)}
            {field('company', 'Company ID', 'text', 80)}
            <label>
              Method
              <select id="al_method" value={draft.method ?? ''} onChange={(e) => setDraft({ ...draft, method: e.target.value })}>
                <option value="">Any</option>
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            {field('route', 'Route contains', 'text', 160)}
            <label>
              Status
              <select id="al_status" value={draft.status ?? ''} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                <option value="">Any</option>
                <option value="2xx">2xx</option>
                <option value="3xx">3xx</option>
                <option value="4xx">4xx</option>
                <option value="5xx">5xx</option>
              </select>
            </label>
            {field('ip', 'IP', 'text', 120)}
            <button className="btn btn-info btn-sm" id="alSearch" type="submit">
              <i className="fas fa-search"></i> Search
            </button>
            <button
              className="btn btn-default btn-sm"
              type="button"
              onClick={() => {
                setDraft(EMPTY)
                setFilters(EMPTY)
              }}
            >
              Clear
            </button>
            <a className="btn btn-default btn-sm" id="alCsv" href={apiLogCsvUrl(filters)}>
              <i className="fas fa-file-csv"></i> Export CSV
            </a>
          </form>

          <div className="ak-card">
            {page.rows.length === 0 ? (
              <p className="ak-note" id="alEmpty" style={{ margin: 0 }}>
                No API call matches.
              </p>
            ) : (
              <table className="table al-table" id="alTable">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>API</th>
                    <th>Key</th>
                    <th>User</th>
                    <th>Company</th>
                    <th>Method</th>
                    <th>Route</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Duration</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {page.rows.map((row) => (
                    <Row key={row.id} row={row} />
                  ))}
                </tbody>
              </table>
            )}
            <div className="al-pager">
              <button className="btn btn-default btn-xs" type="button" disabled={page.page <= 1} onClick={() => go(page.page - 1)}>
                &lsaquo; Newer
              </button>
              <span className="ak-note" id="alPage">
                Page {page.page} of {pages} &middot; {page.total} calls
              </span>
              <button
                className="btn btn-default btn-xs"
                type="button"
                disabled={page.page >= pages}
                onClick={() => go(page.page + 1)}
              >
                Older &rsaquo;
              </button>
            </div>
          </div>

          <div className="ak-card">
            <h4>Retention</h4>
            <p className="ak-note">
              Older calls are deleted every hour, in batches. Between {page.retentionMin} and {page.retentionMax} days.
            </p>
            {page.canWrite ? (
              <>
                <input
                  id="alRetention"
                  type="number"
                  min={page.retentionMin}
                  max={page.retentionMax}
                  value={retention !== '' ? retention : String(page.retentionDays)}
                  disabled={busy}
                  onChange={(e) => setRetention(e.target.value)}
                  style={{ width: 90 }}
                />{' '}
                days{' '}
                <button className="btn btn-info btn-sm" id="alRetentionSave" type="button" disabled={busy} onClick={keep}>
                  Save
                </button>
              </>
            ) : (
              <p id="alRetentionDays">{page.retentionDays} days</p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
